import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, Zap, Upload, Info, Loader2, AlertCircle, Save, X } from 'lucide-react';
import useMarketingAndNotificationStore from '@/stores/AdminStore/MarketingAndNotificationStore';
import { Audience, CampaignData, validateEmails } from '@/services/PagesServices/MarketingAndNotificationService';
import MessageModal from '@/Components/common/MessageModal';
import './ComposeNotification.css';

const ComposeNotification: React.FC = () => {
  const { audiences: storeAudiences, sending, loading, fetchAudiences, createAndSendCampaign } = useMarketingAndNotificationStore();

  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'in-app' | 'both'>('email');
  const [audiences, setAudiences] = useState<Audience[]>(storeAudiences || []);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvEmails, setCsvEmails] = useState<string[]>([]);
  const [validEmails, setValidEmails] = useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [isValidatingEmails, setIsValidatingEmails] = useState(false);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  // Current local date-time as YYYY-MM-DDTHH:mm, used as the min for scheduling.
  const minDateTime = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    title?: string;
    message?: string;
    audience?: string;
    scheduleDate?: string;
  }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to show modal
  const showModal = (type: 'success' | 'error', title: string, message: string) => {
    setModalType(type);
    setModalTitle(title);
    setModalMessage(message);
    setIsModalOpen(true);
  };

  // Helper function to parse and format error messages
  const parseErrorMessage = (error: any): { title: string; message: string } => {
    // Default error
    let title = 'Error';
    let message = 'An unexpected error occurred. Please try again.';

    if (!error) return { title, message };

    // Check if it's a string error
    if (typeof error === 'string') {
      message = error;
      // Try to extract a better title from the message
      if (message.toLowerCase().includes('email')) {
        title = 'Email Error';
      } else if (message.toLowerCase().includes('network')) {
        title = 'Network Error';
      } else if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('forbidden')) {
        title = 'Permission Denied';
      }
      return { title, message };
    }

    // Extract error message from various possible locations
    const errorMsg =
      error.message ||
      error.error?.message ||
      error.data?.message ||
      error.response?.data?.message ||
      error.response?.data?.error ||
      '';

    // If we have a proper error message, use it
    if (errorMsg && errorMsg !== 'HTTP Error') {
      message = errorMsg;

      // Determine appropriate title based on error content
      const lowerMsg = errorMsg.toLowerCase();

      if (lowerMsg.includes('email') && lowerMsg.includes('not found')) {
        title = 'Invalid Email Addresses';
        message = 'Some email addresses from your CSV file do not exist in the system. Please verify the email addresses and try again.';
      } else if (lowerMsg.includes('user') && (lowerMsg.includes('not found') || lowerMsg.includes('does not exist'))) {
        title = 'Users Not Found';
        message = 'Some users could not be found in the system. Please check your audience selection and try again.';
      } else if (lowerMsg.includes('permission') || lowerMsg.includes('forbidden') || lowerMsg.includes('not authorized')) {
        title = 'Permission Denied';
        message = 'You do not have permission to perform this action. Please contact your administrator.';
      } else if (lowerMsg.includes('network') || lowerMsg.includes('connection')) {
        title = 'Network Error';
        message = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (lowerMsg.includes('timeout')) {
        title = 'Request Timeout';
        message = 'The request took too long to complete. Please try again.';
      } else if (lowerMsg.includes('rate limit') || lowerMsg.includes('too many requests')) {
        title = 'Too Many Requests';
        message = 'You have made too many requests. Please wait a moment and try again.';
      } else if (lowerMsg.includes('validation') || lowerMsg.includes('invalid')) {
        title = 'Validation Error';
      } else if (lowerMsg.includes('server error') || lowerMsg.includes('internal error') || lowerMsg.includes('500')) {
        title = 'Server Error';
        message = 'An error occurred on the server. Please try again later.';
      } else if (lowerMsg.includes('unauthorized') || lowerMsg.includes('authentication') || lowerMsg.includes('401')) {
        title = 'Authentication Error';
        message = 'Your session has expired. Please log in again.';
      } else if (lowerMsg.includes('not found') || lowerMsg.includes('404')) {
        title = 'Not Found';
        message = 'The requested resource was not found.';
      } else {
        title = 'Error';
      }
    } else {
      // No useful error message, try to determine from status code
      const status = error.status || error.response?.status;

      switch (status) {
        case 400:
          title = 'Bad Request';
          message = 'Invalid request. Please check your input and try again.';
          break;
        case 401:
          title = 'Authentication Error';
          message = 'Your session has expired. Please log in again.';
          break;
        case 403:
          title = 'Permission Denied';
          message = 'You do not have permission to perform this action.';
          break;
        case 404:
          title = 'Not Found';
          message = 'The requested resource was not found.';
          break;
        case 422:
          title = 'Validation Error';
          message = 'Please check your input and try again.';
          break;
        case 429:
          title = 'Too Many Requests';
          message = 'You have made too many requests. Please wait a moment and try again.';
          break;
        case 500:
          title = 'Server Error';
          message = 'An error occurred on the server. Please try again later.';
          break;
        case 503:
          title = 'Service Unavailable';
          message = 'The service is temporarily unavailable. Please try again later.';
          break;
        default:
          // Keep default error message
          break;
      }
    }

    return { title, message };
  };

  // Fetch audiences on mount if not already loaded
  useEffect(() => {
    if (!storeAudiences || storeAudiences.length === 0) {
      fetchAudiences().catch(err => {
        console.error('Failed to fetch audiences:', err);
        const { title, message } = parseErrorMessage(err);
        showModal('error', title, message || 'Failed to load audiences. Please refresh the page.');
      });
    }
  }, []);

  // Sync store data to local state
  useEffect(() => {
    if (storeAudiences) {
      setAudiences(storeAudiences);
    }
  }, [storeAudiences]);

  const validateForm = (checkSchedule: boolean = true): { isValid: boolean; errors: typeof validationErrors } => {
    const errors: typeof validationErrors = {};

    if (!notificationTitle.trim()) {
      errors.title = 'Notification title is required';
    }

    if (!message.trim()) {
      errors.message = 'Message is required';
    }

    const selectedAudiences = audiences.filter(a => a.checked);
    if (selectedAudiences.length === 0 && csvEmails.length === 0) {
      errors.audience = 'Please select at least one audience or upload a CSV file';
    } else if (csvEmails.length > 0 && invalidEmails.length > 0) {
      errors.audience = `${invalidEmails.length} invalid email(s) detected. Please correct them before sending.`;
    }

    if (checkSchedule && scheduleType === 'schedule' && !scheduleDate) {
      errors.scheduleDate = 'Please select a schedule date';
    } else if (checkSchedule && scheduleType === 'schedule' && scheduleDate < minDateTime) {
      errors.scheduleDate = 'Schedule date and time cannot be in the past';
    }

    setValidationErrors(errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const resetForm = () => {
    setNotificationTitle('');
    setMessage('');
    setNotificationType('email');
    setAudiences(storeAudiences?.map(a => ({ ...a, checked: false })) || []);
    setUploadedFile(null);
    setCsvEmails([]);
    setValidEmails([]);
    setInvalidEmails([]);
    setScheduleType('now');
    setScheduleDate('');
    setValidationErrors({});
  };

  // Validate emails against users table
  const validateEmailsInSystem = async (emails: string[]) => {
    setIsValidatingEmails(true);
    try {
      const response = await validateEmails(emails);

      if (response.success && response.data) {
        const { validEmails: valid, invalidEmails: invalid } = response.data;
        setValidEmails(valid);
        setInvalidEmails(invalid);

        if (invalid.length > 0 && valid.length === 0) {
          // All emails are invalid
          showModal(
            'error',
            'Invalid Email Addresses',
            `None of the ${emails.length} email addresses exist in the system. All emails are invalid users. Please verify and upload a corrected CSV file.`
          );
          return false;
        } else if (invalid.length > 0) {
          // Some emails are invalid
          showModal(
            'error',
            'Some Invalid Email Addresses',
            `${invalid.length} out of ${emails.length} email addresses are not valid users. The notification will only be sent to the ${valid.length} valid email(s). Please remove or correct the invalid emails and re-upload the CSV if needed.`
          );
          return false;
        } else {
          // All emails are valid
          showModal(
            'success',
            'All Emails Valid',
            `All ${valid.length} email address(es) have been verified and exist in the system.`
          );
          return true;
        }
      } else {
        showModal('error', 'Validation Failed', response.error?.message || 'Failed to validate email addresses');
        return false;
      }
    } catch (error) {
      console.error('Email validation error:', error);
      const { title, message } = parseErrorMessage(error);
      showModal('error', title, message);
      return false;
    } finally {
      setIsValidatingEmails(false);
    }
  };

  const handleSaveAsDraft = async () => {
    // Validation (draft doesn't require schedule date)
    const validation = validateForm(false);
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      showModal('error', 'Validation Error', firstError || 'Please fix the errors in the form');
      return;
    }

    // If there are CSV emails, check if any are invalid
    if (csvEmails.length > 0 && invalidEmails.length > 0) {
      showModal(
        'error',
        'Invalid Emails Detected',
        `Cannot save draft with ${invalidEmails.length} invalid email address(es). Please remove or correct the invalid emails and re-upload the CSV file.`
      );
      return;
    }

    setIsSavingDraft(true);

    try {
      const selectedAudiences = audiences.filter(a => a.checked);

      // Use validEmails instead of all csvEmails
      const emailsToUse = csvEmails.length > 0 ? (validEmails.length > 0 ? validEmails : csvEmails) : undefined;

      // Build campaign data for draft
      const campaignData: CampaignData = {
        title: notificationTitle.trim(),
        subject: notificationTitle.trim(),
        message: message.trim(),
        targetAudience: {
          roles: selectedAudiences.map(a => a.role).filter((r): r is number => r !== undefined),
          specificUsers: emailsToUse,
        },
        deliveryMethod: notificationType,
        scheduledFor: null,
      };

      // Save as draft (sendImmediately = false, no schedule)
      const result = await createAndSendCampaign(campaignData, false);

      if (result.success) {
        showModal('success', 'Draft Saved', 'Notification saved as draft successfully!');
        resetForm();
      } else {
        const { title, message } = parseErrorMessage(result.error);
        showModal('error', title, message);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      const { title, message } = parseErrorMessage(error);
      showModal('error', title, message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    const validation = validateForm(true);
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      showModal('error', 'Validation Error', firstError || 'Please fix the errors in the form');
      return;
    }

    // If there are CSV emails, check if any are invalid
    if (csvEmails.length > 0 && invalidEmails.length > 0) {
      showModal(
        'error',
        'Invalid Emails Detected',
        `Cannot send notification with ${invalidEmails.length} invalid email address(es). Please remove or correct the invalid emails and re-upload the CSV file.`
      );
      return;
    }

    const selectedAudiences = audiences.filter(a => a.checked);

    // Use validEmails instead of all csvEmails
    const emailsToUse = csvEmails.length > 0 ? (validEmails.length > 0 ? validEmails : csvEmails) : undefined;

    // Build campaign data
    const campaignData: CampaignData = {
      title: notificationTitle.trim(),
      subject: notificationTitle.trim(),
      message: message.trim(),
      targetAudience: {
        roles: selectedAudiences.map(a => a.role).filter((r): r is number => r !== undefined),
        specificUsers: emailsToUse,
      },
      deliveryMethod: notificationType,
      scheduledFor: scheduleType === 'schedule' ? scheduleDate.replace('T', ' ') : null,
    };

    const sendImmediately = scheduleType === 'now';
    const result = await createAndSendCampaign(campaignData, sendImmediately);

    if (result.success) {
      showModal(
        'success',
        sendImmediately ? 'Notification Sent' : 'Notification Scheduled',
        sendImmediately
          ? 'Notification sent successfully!'
          : 'Notification scheduled successfully!'
      );
      resetForm();
    } else {
      console.error('Error sending notification:', result.error);
      const { title, message } = parseErrorMessage(result.error);
      showModal('error', title, message);
    }
  };

  const handleAudienceToggle = (id: string) => {
    setAudiences(audiences.map(aud =>
      aud.id === id ? { ...aud, checked: !aud.checked } : aud
    ));
    // Clear audience error when user toggles an audience
    if (validationErrors.audience) {
      setValidationErrors(prev => ({ ...prev, audience: undefined }));
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showModal('error', 'File Too Large', 'File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        showModal('error', 'Invalid File Type', 'Only CSV files are supported');
        return;
      }

      // Read and parse CSV file
      try {
        const text = await file.text();
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);

        // Extract emails - look for email pattern in each line
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const extractedEmails: string[] = [];

        for (const line of lines) {
          const matches = line.match(emailRegex);
          if (matches) {
            extractedEmails.push(...matches);
          }
        }

        // Remove duplicates
        const uniqueEmails = Array.from(new Set(extractedEmails));

        if (uniqueEmails.length === 0) {
          showModal('error', 'No Emails Found', 'No valid email addresses found in CSV file');
          return;
        }

        // Validate emails against the system
        setCsvEmails(uniqueEmails);
        setUploadedFile(file);

        // Validate emails in the system
        await validateEmailsInSystem(uniqueEmails);

        // Clear audience error when CSV is uploaded
        if (validationErrors.audience) {
          setValidationErrors(prev => ({ ...prev, audience: undefined }));
        }
      } catch (error) {
        console.error('CSV parse error:', error);
        const { title, message } = parseErrorMessage(error);
        showModal('error', title, message || 'Failed to read CSV file. Please check the file format.');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showModal('error', 'File Too Large', 'File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        showModal('error', 'Invalid File Type', 'Only CSV files are supported');
        return;
      }

      // Read and parse CSV file
      try {
        const text = await file.text();
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);

        // Extract emails - look for email pattern in each line
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const extractedEmails: string[] = [];

        for (const line of lines) {
          const matches = line.match(emailRegex);
          if (matches) {
            extractedEmails.push(...matches);
          }
        }

        // Remove duplicates
        const uniqueEmails = Array.from(new Set(extractedEmails));

        if (uniqueEmails.length === 0) {
          showModal('error', 'No Emails Found', 'No valid email addresses found in CSV file');
          return;
        }

        // Validate emails against the system
        setCsvEmails(uniqueEmails);
        setUploadedFile(file);

        // Validate emails in the system
        await validateEmailsInSystem(uniqueEmails);

        // Clear audience error when CSV is uploaded
        if (validationErrors.audience) {
          setValidationErrors(prev => ({ ...prev, audience: undefined }));
        }
      } catch (error) {
        console.error('CSV parse error:', error);
        const { title, message } = parseErrorMessage(error);
        showModal('error', title, message || 'Failed to read CSV file. Please check the file format.');
      }
    }
  };

  const handleDownloadSampleCSV = () => {
    // Create sample CSV content
    const csvContent = `email
john.doe@example.com
jane.smith@example.com
admin@company.com
user1@domain.com
user2@domain.com`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_emails.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const selectedCount = audiences.filter(a => a.checked).length;

  return (
    <div className="admin-marketing-compose-content">
      <h2 className="admin-marketing-section-title">Compose Notification</h2>
      <p className="admin-marketing-section-subtitle">Create and send notifications to your users</p>

      <div className="admin-marketing-compose-layout">
        <div className="left-panel">
          <div className="admin-marketing-compose-card">
            <h3 className="admin-marketing-card-title">Message Details</h3>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Notification Title</label>
              <input
                type="text"
                className={`admin-marketing-form-input ${validationErrors.title ? 'error' : ''}`}
                placeholder="e.g., New Course Available"
                value={notificationTitle}
                onChange={(e) => {
                  setNotificationTitle(e.target.value);
                  if (validationErrors.title) {
                    setValidationErrors(prev => ({ ...prev, title: undefined }));
                  }
                }}
              />
              {validationErrors.title && (
                <div className="admin-marketing-error-message">
                  <AlertCircle size={14} />
                  <span>{validationErrors.title}</span>
                </div>
              )}
            </div>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Message</label>
              <textarea
                className={`admin-marketing-form-textarea ${validationErrors.message ? 'error' : ''}`}
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (validationErrors.message) {
                    setValidationErrors(prev => ({ ...prev, message: undefined }));
                  }
                }}
                rows={4}
              />
              {validationErrors.message && (
                <div className="admin-marketing-error-message">
                  <AlertCircle size={14} />
                  <span>{validationErrors.message}</span>
                </div>
              )}
            </div>
          </div>

          <div className="admin-marketing-compose-card">
            <h3 className="admin-marketing-card-title">Delivery Settings</h3>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Notification Type</label>
              <div className="admin-marketing-notification-types">
                <button
                  className={`admin-marketing-type-btn ${notificationType === 'email' ? 'admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('email')}
                >
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button
                  className={`admin-marketing-type-btn ${notificationType === 'in-app' ? 'admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('in-app')}
                >
                  <Bell size={20} />
                  <span>In-App</span>
                </button>
                <button
                  className={`admin-marketing-type-btn ${notificationType === 'both' ? 'admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('both')}
                >
                  <Zap size={20} />
                  <span>Both</span>
                </button>
              </div>
            </div>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Select Audience</label>
              <div className="admin-marketing-audience-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    <Loader2 size={24} className="admin-marketing-spinner" style={{ margin: '0 auto 10px' }} />
                    <p>Loading audiences...</p>
                  </div>
                ) : audiences && audiences.length > 0 ? (
                  audiences.map(audience => (
                    <label key={audience.id} className="admin-marketing-audience-item">
                      <input
                        type="checkbox"
                        checked={audience.checked}
                        onChange={() => handleAudienceToggle(audience.id)}
                        className="admin-marketing-audience-checkbox"
                      />
                      <div className="admin-marketing-audience-info">
                        <span className="admin-marketing-audience-name">{audience.name}</span>
                        <span className="admin-marketing-audience-count">{audience.userCount} users</span>
                      </div>
                    </label>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
                    <AlertCircle size={24} style={{ margin: '0 auto 10px' }} />
                    <p>No audiences available. Please check your connection.</p>
                    <button
                      onClick={() => fetchAudiences()}
                      style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
              {validationErrors.audience && (
                <div className="admin-marketing-error-message">
                  <AlertCircle size={14} />
                  <span>{validationErrors.audience}</span>
                </div>
              )}
            </div>

            <div className="admin-marketing-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="admin-marketing-form-label" style={{ margin: 0 }}>
                  Upload CSV with Emails (Optional)
                </label>
                <button
                  type="button"
                  onClick={handleDownloadSampleCSV}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                    color: '#3b82f6',
                    background: 'transparent',
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#3b82f6';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#3b82f6';
                  }}
                >
                  <Upload size={14} />
                  Download Sample CSV
                </button>
              </div>
              <div
                className="admin-marketing-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !uploadedFile && fileInputRef.current?.click()}
                style={{ cursor: uploadedFile ? 'default' : 'pointer' }}
              >
                <div className="admin-marketing-upload-icon">
                  <Upload size={24} />
                </div>
                {uploadedFile ? (
                  <>
                    <p className="admin-marketing-upload-text">
                      {uploadedFile.name}
                    </p>
                    {isValidatingEmails ? (
                      <p className="admin-marketing-upload-subtext" style={{ color: '#3b82f6' }}>
                        <Loader2 size={14} className="admin-marketing-spinner" style={{ display: 'inline-block', marginRight: '6px' }} />
                        Validating emails...
                      </p>
                    ) : (
                      <>
                        {invalidEmails.length > 0 ? (
                          <>
                            <p className="admin-marketing-upload-subtext" style={{ color: '#10b981' }}>
                              ✓ {validEmails.length} valid email(s)
                            </p>
                            <p className="admin-marketing-upload-subtext" style={{ color: '#ef4444', marginTop: '4px' }}>
                              ✗ {invalidEmails.length} invalid email(s)
                            </p>
                          </>
                        ) : (
                          <p className="admin-marketing-upload-subtext" style={{ color: '#10b981' }}>
                            ✓ All {validEmails.length || csvEmails.length} email(s) verified
                          </p>
                        )}
                      </>
                    )}
                    <button
                      className="admin-marketing-upload-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFile(null);
                        setCsvEmails([]);
                        setValidEmails([]);
                        setInvalidEmails([]);
                      }}
                      style={{ background: '#ef4444' }}
                    >
                      <X size={16} style={{ marginRight: '4px' }} />
                      Clear File
                    </button>
                  </>
                ) : (
                  <>
                    <p className="admin-marketing-upload-text">
                      Drop your CSV file here
                    </p>
                    <p className="admin-marketing-upload-subtext">
                      or click to browse from your computer
                    </p>
                    <button
                      className="admin-marketing-upload-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Select File
                    </button>
                    <p className="admin-marketing-upload-hint">Supports .csv files up to 10MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="admin-marketing-form-row">
              <div className="admin-marketing-form-group admin-marketing-form-group-half">
                <label className="admin-marketing-form-label">Schedule</label>
                <select
                  className="admin-marketing-form-select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <option value="now">Send Now</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
              <div className="admin-marketing-form-group admin-marketing-form-group-half">
                <label className="admin-marketing-form-label">Date & Time *</label>
                <input
                  type="datetime-local"
                  className={`admin-marketing-form-input ${validationErrors.scheduleDate ? 'error' : ''}`}
                  value={scheduleDate}
                  min={minDateTime}
                  onChange={(e) => {
                    setScheduleDate(e.target.value);
                    if (validationErrors.scheduleDate) {
                      setValidationErrors(prev => ({ ...prev, scheduleDate: undefined }));
                    }
                  }}
                  disabled={scheduleType === 'now'}
                />
                {validationErrors.scheduleDate && (
                  <div className="admin-marketing-error-message">
                    <AlertCircle size={14} />
                    <span>{validationErrors.scheduleDate}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="admin-marketing-preview-card">
            <div className="admin-marketing-preview-header">
              <Mail size={16} className="admin-marketing-preview-icon" />
              <span>Email Preview</span>
            </div>

            <div className="admin-marketing-preview-content">
              <div className="admin-marketing-preview-from">
                <span className="admin-marketing-preview-label">From:</span>
                <span>noreply@company.com</span>
              </div>
              <div className="admin-marketing-preview-to">
                <span className="admin-marketing-preview-label">To:</span>
                <span>Select audience</span>
              </div>

              <div className="admin-marketing-preview-message">
                <h3 className="admin-marketing-preview-title">
                  {notificationTitle || 'Your notification title'}
                </h3>
                <p className="admin-marketing-preview-text">
                  {message || 'Your message will appear here...'}
                </p>
              </div>
            </div>

            <div className="admin-marketing-ready-banner">
              <Info size={16} />
              <div className="admin-marketing-ready-content">
                <span className="admin-marketing-ready-title">Ready to Send</span>
                <ul className="admin-marketing-ready-list">
                  <li>• {selectedCount} audience{selectedCount !== 1 ? 's' : ''} selected</li>
                  {csvEmails.length > 0 && (
                    <>
                      {validEmails.length > 0 && <li style={{ color: '#10b981' }}>• {validEmails.length} valid email(s) from CSV</li>}
                      {invalidEmails.length > 0 && <li style={{ color: '#ef4444' }}>• {invalidEmails.length} invalid email(s) will be skipped</li>}
                      {validEmails.length === 0 && invalidEmails.length === 0 && <li>• {csvEmails.length} email(s) from CSV (validating...)</li>}
                    </>
                  )}
                  <li>• Type: {notificationType}</li>
                  <li>• {scheduleType === 'now' ? 'Send immediately' : `Scheduled: ${scheduleDate}`}</li>
                </ul>
              </div>
            </div>

            <div className="admin-marketing-actions">
              <button
                className="admin-marketing-draft-btn"
                onClick={handleSaveAsDraft}
                disabled={isSavingDraft || sending}
              >
                {isSavingDraft ? (
                  <>
                    <Loader2 size={18} className="admin-marketing-spinner" />
                    Saving Draft...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save as Draft
                  </>
                )}
              </button>
              <button
                className="admin-marketing-send-btn"
                onClick={handleSubmit}
                disabled={sending || isSavingDraft}
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="admin-marketing-spinner" />
                    {scheduleType === 'now' ? 'Sending...' : 'Scheduling...'}
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    {scheduleType === 'now' ? 'Send Notification' : 'Schedule Notification'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <MessageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={modalType}
        title={modalTitle}
        message={modalMessage}
      />
    </div>
  );
};

export default ComposeNotification;
